<script lang="ts">
	import { invalidateAll } from '$app/navigation';
	import { debounce } from 'lodash-es';
	import { fly } from 'svelte/transition';
	import { currentEvent, isUserAttending } from '$lib/currentEvent';
	import { Attending, ContactPreference } from '$lib/eventTypes';
	import IconTrash from '$lib/icons/IconTrash.svelte';
	import InnerInputButton from '$lib/InnerInputButton.svelte';
	import LoadingButton from '$lib/LoadingButton.svelte';
	import InputText, { inputClasses } from '$lib/InputText.svelte';
	import IconWarning from '~icons/ep/warning';
	import { user } from '$lib/stores/user';
	import { preventDefault } from 'svelte/legacy';
	import {
		removeMe,
		setMeAsAttending,
		setMeAsMaybeAttending,
		setMeAsNotAttending,
		setMeToWaitlist,
	} from './page.telefunc';
	import Modal from '$lib/Modal.svelte';
	import { paypalLink } from '$lib/common';
	import IconPaypal from '~icons/ic/baseline-paypal';
	import { sanitizeNameAndCompanions } from './eventCommon';
	import AlertBanner from '$lib/AlertBanner.svelte';
	import { userEmail } from '$lib/stores/userEmail';
	import { userPhone } from '$lib/stores/userPhone';
	import type { GetGuestBySlugType } from '$lib/server/supabase';
	import MultiSelectInput from '$lib/components/MultiSelectInput.svelte';
	import { displayPrice } from '$lib/commonLogic';
	import * as m from '$lib/paraglide/messages.js';
	import InputLongText from '$lib/InputLongText.svelte';

	type Props = {
		showModal: boolean;
		newGuestAttending: Attending | undefined;
		guestFromServer: GetGuestBySlugType;
		isMaybeAnswerEnabled: boolean;
	};

	let {
		showModal = $bindable(false),
		newGuestAttending = undefined,
		guestFromServer,
		isMaybeAnswerEnabled,
	}: Props = $props();

	let attendingForm = $state<HTMLFormElement>();
	let attending = $state((guestFromServer?.attending as Attending) ?? newGuestAttending ?? Attending.UNKNOWN);
	let guest = $derived(
		guestFromServer ?? {
			id: $user.id,
			name: $user.name,
			attending: attending,
			created: null,
			comment: '',
			companions: [],
			bringItems: [],
			paidAllegedly: undefined,
			answers: {},
		}
	);
	let comment = $state(guest?.comment ?? '');
	let okButtonLoading = $state(false);

	let contactPreference = $state<ContactPreference>(guestFromServer?.contactPreference);
	let contactDetails = $state(guestFromServer?.phone ?? guestFromServer?.email ?? '');

	$effect(() => {
		if (contactPreference === ContactPreference.EMAIL) {
			contactDetails = $userEmail;
		} else if (contactPreference === ContactPreference.PHONE) {
			contactDetails = $userPhone;
		}
	});

	let name = $state(guest?.name ?? '');
	const setName = () => (!name ? (name = guest?.name ?? $user.name ?? '') : name);
	$effect(() => {
		if (showModal) setName();
	});

	let eventFull = $derived($currentEvent.maxGuests && $currentEvent.guests.length >= ($currentEvent.maxGuests ?? -1));
	let showAddedToWaitlistDialog = $state(false);

	let isBringingItems = $state(false);
	const placeholderItem = { name: '', id: undefined as string | undefined };
	let bringItems = $state<(typeof placeholderItem)[]>([]);

	$effect(() => fillBringItemsFromEvent($currentEvent));
	$effect(() => {
		if (bringItems.length > 1) isBringingItems = true;
		if (bringItems.length < 1) isBringingItems = false;
	});

	function fillBringItemsFromEvent(event: typeof $currentEvent) {
		bringItems = [
			...event.items.filter((x) => x.userId === $user.id).map((x) => ({ ...placeholderItem, ...x })),
			{ ...placeholderItem },
		];
	}

	const onBringItemTextChanged = debounce(() => {
		if (bringItems[bringItems.length - 1]?.name.trim() !== '') {
			bringItems = [...bringItems, { ...placeholderItem }];
		}
	}, 400);

	let isBringingCompanions = $state(false);
	let companions = $state<string[]>([]);
	const placeholderCompanion = '';

	$effect(() => fillCompanionsFromEvent($currentEvent));
	$effect(() => {
		if (companions.length > 1 || companions.some((x) => x.trim())) {
			isBringingCompanions = true;
			return;
		}
		if (companions.length < 1) isBringingCompanions = false;
	});

	function fillCompanionsFromEvent(event: typeof $currentEvent) {
		companions = [...(event.guests.find((x) => x.user?.id === $user.id)?.companions ?? []), placeholderCompanion];
	}

	const onCompanionTextChanged = debounce(() => {
		if (companions[companions.length - 1]?.trim() !== '') {
			companions = [...companions, placeholderCompanion];
		}
	}, 400);

	$effect(() => {
		if (showModal) {
			loadComment();
			loadAttending();
		}
	});

	const loadAttending = () => (attending = newGuestAttending ?? isUserAttending($user.id, $currentEvent));
	const loadComment = () => (comment = guest?.comment ?? '');

	async function submit(payedAllegedly: number | undefined) {
		okButtonLoading = true;
		try {
			if (!$user.name || !$user.authUserId) {
				$user.name = name;
			}
			let email: string | undefined;
			let phone: string | undefined;
			if (contactPreference === ContactPreference.EMAIL) {
				email = contactDetails;
				$userEmail = contactDetails;
			} else if (contactPreference === ContactPreference.PHONE) {
				phone = contactDetails;
				$userPhone = contactDetails;
			}

			const answers = questions.map((x) => ({ questionId: x.id, answer: x.answer }));

			if (attending === Attending.ATTENDING) {
				const res = await setMeAsAttending($currentEvent.id, {
					name,
					comment,
					bringItems: bringItems.map((x) => ({ id: x.id, name: x.name })),
					companions,
					payedAllegedly,
					contactPreference,
					email,
					phone,
					answers,
				});
				$user.name = res.name;
				name = $user.name;
				attending = res.attending;
			} else if (attending === Attending.MAYBE) {
				const res = await setMeAsMaybeAttending($currentEvent.id, {
					name,
					comment,
					bringItems: bringItems.map((x) => ({ id: x.id, name: x.name })),
					companions,
					contactPreference,
					email,
					phone,
					answers,
				});
				$user.name = res.name;
				name = $user.name;
				attending = res.attending;
			} else if (attending === Attending.WAITLIST) {
				const res = await setMeToWaitlist($currentEvent.id, {
					name,
					comment,
					bringItems: bringItems.map((x) => ({ id: x.id, name: x.name })),
					companions,
					contactPreference,
					email,
					phone,
					answers,
				});
				$user.name = res.name;
				name = $user.name;
				attending = res.attending;
			} else if (attending === Attending.NOT_ATTENDING) {
				await setMeAsNotAttending($currentEvent.id, { name, comment });
			}
			await invalidateAll();
			if (newGuestAttending) newGuestAttending = undefined;
		} finally {
			showModal = false;
			okButtonLoading = false;
		}
		if (attending === Attending.WAITLIST) {
			showAddedToWaitlistDialog = true;
		}
	}

	async function cancel() {
		showModal = false;
		console.log('cancel', guest);
		if (!$user.name?.trim()) {
			console.log('remove guest', guest);
			await removeMe($currentEvent.id);
			invalidateAll();
		}
	}

	function openPaymentModal() {
		if (!attendingForm?.reportValidity()) return;

		showPaymentModal = true;

		({ name, companions } = sanitizeNameAndCompanions(name, companions));
		attendingPersons = companions.filter((x) => x.trim()).length + 1;
	}

	const displayAmount = (price: number | null) => price?.toLocaleString('de-DE') ?? '0';
	let showPaymentModal = $state(false);
	let showDidNotPayModal = $state(false);
	let ownerName = $derived($currentEvent.owners[0]?.user?.name);
	let guestPaidAlreadyButAddedCompanions = $derived(
		(guest?.paidAllegedly ?? 0) > 0 &&
			sanitizeNameAndCompanions(name, companions).companions.length > guest.companions.length
	);

	let attendingPromptHasPaymentStep = $derived(
		$currentEvent.paypalUsername &&
			($currentEvent.price || $currentEvent.maxPrice) &&
			attending === Attending.ATTENDING &&
			(guest.paidAllegedly == undefined || guestPaidAlreadyButAddedCompanions)
	);

	let attendingPersons = $state(1);

	let flexPrice = $state($currentEvent.price);
	let totalPrice = $derived((flexPrice ?? 0) * attendingPersons);
	let diffPrice = $derived(totalPrice - (guest.paidAllegedly ?? 0));
	let totalPriceStr = $derived(attendingPersons > 1 ? `${displayAmount(totalPrice)}€` : displayPrice($currentEvent));

	let questions = $state(initQuestions());

	function initQuestions() {
		console.log('initQuestions', $currentEvent.questions);
		return $currentEvent.questions.map((x) => ({
			...x,
			answer: guest?.answers?.[x.id] ?? '',
		}));
	}
</script>

{#if showModal}
	<Modal header={false} onclose={cancel} classs="max-w-md">
		<form onsubmit={preventDefault(() => submit(undefined))} bind:this={attendingForm}>
			{#if !newGuestAttending}
				<div class="mb-5">
					<h4 class="label mb-2">{m.attendingPrompt_participation()}</h4>

					{#if guest.attending === Attending.WAITLIST}
						<input
							bind:group={attending}
							value={Attending.WAITLIST}
							id="attend-waitlist"
							type="radio"
							name="attending"
							data-testid="attend-prompt-waitlist-input"
							class="radioBox"
						/>
						<label for="attend" class="ml-2">{m.attendingPrompt_waitlist()}</label>
					{:else}
						<div>
							<input
								bind:group={attending}
								value={Attending.ATTENDING}
								id="attend"
								type="radio"
								name="attending"
								data-testid="attend-prompt-attending-input"
								class="radioBox"
							/>
							<label for="attend" class="ml-2">
								{#if $currentEvent.isPublic}
									{m.attendingPrompt_register()}
								{:else}
									{m.attendingPrompt_accept()}
								{/if}
							</label>
						</div>
					{/if}

					{#if isMaybeAnswerEnabled}
						<div class="mt-2">
							<input
								bind:group={attending}
								value={Attending.MAYBE}
								id="attend-maybe"
								type="radio"
								name="attending"
								data-testid="attend-prompt-maybe-input"
								class="radioBox"
							/>
							<label for="attend-maybe" class="ml-2">{m.attendingPrompt_maybe()}</label>
						</div>
					{/if}

					<div class="mt-2">
						<input
							bind:group={attending}
							value={Attending.NOT_ATTENDING}
							id="do-not-attend"
							type="radio"
							name="attending"
							data-testid="attend-prompt-decline-input"
							class="radioBox"
						/>
						<label for="do-not-attend" class="ml-2">
							{#if $currentEvent.isPublic}
								{m.attendingPrompt_unregister()}
							{:else}
								{m.attendingPrompt_decline()}
							{/if}
						</label>
					</div>
				</div>
			{/if}

			<div class="mb-2">
				<label class="label mb-2 block" for="input-userName">{m.attendingPrompt_nameLabel()}</label>
				<InputText
					bind:value={name}
					id="input-userName"
					required
					name="fullname"
					placeholder=" "
					pattern="[\s\S]*\S[\s\S]*"
					autocomplete="name"
					data-testid="attend-prompt-name-input"
					maxlength={40}
				/>
			</div>

			{#if [Attending.ATTENDING, Attending.MAYBE].includes(attending)}
				{#if $currentEvent.companionsEnabled}
					{#if !isBringingCompanions}
						<div class="relative my-6 flex items-start">
							<div class="flex h-5 items-center">
								<input bind:checked={isBringingCompanions} type="checkbox" id="bringing-companions" class="checkBox" />
							</div>
							<div class="ml-3 text-sm">
								<label for="bringing-companions" class="label">{m.attendingPrompt_withCompanions()}</label>
							</div>
						</div>
					{:else}
						<div in:fly class="mt-4">
							<label class="label mb-2 inline-block" for={`input-companion-0`}>
								{m.attendingPrompt_companionName()}
							</label>
							<span class="sub-label pl-2">{m.input_optional()}</span>
							{#each companions as companion, i}
								<div in:fly class="mb-2">
									<InputText
										bind:value={companions[i]}
										id={`input-companion-${i}`}
										placeholder={i === 0 ? ' ' : m.attendingPrompt_companionWhoElse()}
										autocomplete="off"
										maxlength={40}
										onkeyup={onCompanionTextChanged}
									>
										{#snippet right()}
											{#if i !== companions.length - 1 || i === 0}
												<InnerInputButton
													onclick={() => {
														companions = companions.filter((x) => x !== companion);
														onCompanionTextChanged();
													}}
													title={m.delete_()}
													type="button"
												>
													<IconTrash />
												</InnerInputButton>
											{/if}
										{/snippet}
									</InputText>
								</div>
							{/each}
						</div>
					{/if}
				{/if}

				{#if $currentEvent.bringItemsEnabled}
					{#if !isBringingItems}
						<div class="relative my-6 flex items-start">
							<div class="flex h-5 items-center">
								<input bind:checked={isBringingItems} type="checkbox" id="bringing-items" class="checkBox" />
							</div>
							<div class="ml-3 text-sm">
								<label for="bringing-items" class="label">{m.attendingPrompt_bringItems()}</label>
							</div>
						</div>
					{:else}
						<div in:fly class="mt-4">
							<label class="label mb-2 inline-block" for="input-item-0">{m.attendingPrompt_whatBring()}</label>
							<span class="sub-label pl-2">{m.input_optional()}</span>
							{#each bringItems as bringItem, i}
								<div in:fly class="mb-2">
									<InputText
										bind:value={bringItem.name}
										id={`input-item-${i}`}
										placeholder={i === 0 ? m.attendingPrompt_bringExample() : m.attendingPrompt_bringWhatElse()}
										autocomplete="off"
										maxlength={40}
										onkeyup={onBringItemTextChanged}
									>
										{#snippet right()}
											{#if i !== bringItems.length - 1 || i === 0}
												<InnerInputButton
													onclick={() => {
														bringItems = bringItems.filter((x) => x !== bringItem);
														onBringItemTextChanged();
													}}
													title={m.delete_()}
													type="button"
												>
													<IconTrash />
												</InnerInputButton>
											{/if}
										{/snippet}
									</InputText>
								</div>
							{/each}
						</div>
					{/if}
				{/if}

				<div class="my-4">
					<label class="label mb-2 inline-block" for="contact-preference-input">
						{m.attendingPrompt_receiveUpdates()}
					</label>
					<select
						bind:value={contactPreference}
						id="contact-preference-input"
						name="contact-preference-input"
						class={`${inputClasses} w-full`}
						required
						data-testid="contact-preference-input"
					>
						<option disabled selected value={undefined}>{m.attendingPrompt_selectMethod()}</option>
						<option value={ContactPreference.PHONE}>{m.attendingPrompt_sms()}</option>
						<option value={ContactPreference.EMAIL}>{m.attendingPrompt_email()}</option>
						{#if !eventFull}
							<option value={ContactPreference.NONE}>{m.attendingPrompt_noUpdates()}</option>
						{/if}
					</select>
				</div>

				{#if contactPreference === 'none'}
					<AlertBanner type="info">
						<p>
							<b class="font-semibold">{m.attendingPrompt_noUpdatesWarning()}</b>
							{m.attendingPrompt_noUpdatesDetail()}
						</p>
						<p class="mt-1">
							{m.attendingPrompt_noUpdatesInfo()}
						</p>
						<p class="mt-1">{m.attendingPrompt_noUpdatesPrivacy()}</p>
					</AlertBanner>
				{:else if contactPreference}
					<div class="mb-2 mt-4">
						<label class="label mb-2 inline-block" for="contact-details-input">
							{contactPreference === ContactPreference.EMAIL
								? m.attendingPrompt_email()
								: contactPreference === ContactPreference.PHONE
									? m.attendingPrompt_phoneNumber()
									: ''}
						</label>
						<InputText
							type={contactPreference === ContactPreference.EMAIL
								? 'email'
								: contactPreference === ContactPreference.PHONE
									? 'tel'
									: ''}
							bind:value={contactDetails}
							required
							id="contact-details-input"
							name="contact-details-input"
							autocomplete={contactPreference === ContactPreference.EMAIL
								? 'email'
								: contactPreference === ContactPreference.PHONE
									? 'tel'
									: ''}
							maxlength={200}
							data-testid="contact-details-input"
						/>
					</div>
				{/if}

				{#if questions.length > 0}
					<div class="mb-2">
						{#each questions as question, i}
							<div class="mt-4">
								<label class="label inline-block" for={`input-question-${i}`}> {question.text} </label>
								{#if !question.mandatory}
									<span class="sub-label pl-2">{m.input_optional()}</span>
								{/if}
								<p class="mb-2 text-sm text-gray-600">{question.description ?? ''}</p>
								{#if question.possibleAnswers.length > 0 && question.multipleAnswers}
									<MultiSelectInput
										bind:value={question.answer}
										options={question.possibleAnswers}
										required={question.mandatory}
										class="w-full"
									/>
								{:else if question.possibleAnswers.length > 0 && !question.multipleAnswers}
									<select
										bind:value={question.answer}
										id={`input-question-${i}`}
										name={`input-question-${i}`}
										class={`${inputClasses} w-full`}
										required={question.mandatory}
										data-testid={`attend-prompt-question-${i}-input`}
									>
										<option disabled={question.mandatory} selected value></option>
										{#each question.possibleAnswers as possibleAnswer}
											<option value={possibleAnswer}>{possibleAnswer}</option>
										{/each}
									</select>
								{:else}
									<InputLongText
										id={`input-question-${i}`}
										bind:value={question.answer}
										autocomplete="off"
										required={question.mandatory}
										maxlength={3000}
										data-testid={`attend-prompt-question-${i}-input`}
									/>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
			{/if}

			<div class="mb-2 mt-4">
				<label class="label mb-2 inline-block" for="input-comment">{m.attendingPrompt_comment()}</label>
				<span class="sub-label pl-2">
					{$currentEvent.guestVisibility === 'public'
						? m.attendingPrompt_commentOptionalVisibleToEveryone()
						: $currentEvent.guestVisibility === 'guests' || $currentEvent.guestVisibility === 'guests_count_public'
							? m.attendingPrompt_commentOptionalVisibleToGuests()
							: m.attendingPrompt_commentOptionalVisibleToHosts()}
				</span>
				<InputText
					bind:value={comment}
					id="input-comment"
					placeholder={m.attendingPrompt_commentExample()}
					autocomplete="off"
					maxlength={200}
					data-testid="attend-prompt-comment-input"
				/>
			</div>

			<div class="mt-6 flex flex-row justify-end gap-6">
				<LoadingButton
					onclick={cancel}
					disabled={okButtonLoading}
					class="btn btn-secondary"
					data-testid="attend-prompt-cancel-btn"
				>
					{m.attendingPrompt_cancel()}
				</LoadingButton>

				{#if attendingPromptHasPaymentStep && !eventFull}
					<LoadingButton
						onclick={openPaymentModal}
						loading={okButtonLoading}
						type="button"
						data-testid="attend-prompt-ok-btn"
						class="btn btn-primary">{m.attendingPrompt_next()}</LoadingButton
					>
				{:else if eventFull && attending !== Attending.NOT_ATTENDING && (guest.attending === Attending.NOT_ATTENDING || guest.attending === Attending.UNKNOWN || !guest.attending)}
					<LoadingButton
						type="submit"
						loading={okButtonLoading}
						class="btn btn-primary"
						data-testid="attend-prompt-ok-btn"
					>
						{m.attendingPrompt_addToWaitlist()}
					</LoadingButton>
				{:else}
					<LoadingButton
						type="submit"
						loading={okButtonLoading}
						class="btn btn-primary"
						data-testid="attend-prompt-ok-btn"
					>
						{m.attendingPrompt_submit()}
					</LoadingButton>
				{/if}
			</div>
		</form>
	</Modal>
{/if}

{#if showPaymentModal}
	<Modal onclose={() => (showPaymentModal = false)} classs="max-w-sm" header={true} paddingAndMargin={false}>
		<div class="-mt-4 px-4 pb-4">
			<div class="">
				{#if $currentEvent.maxPrice}
					<div class="">
						<div class="label">Wieviel ist dir der Event wert?</div>

						<div class="flex items-center gap-4">
							<input
								id="default-range"
								type="range"
								bind:value={flexPrice}
								min={$currentEvent.price ?? 0}
								max={($currentEvent.maxPrice ?? 0) + 1}
								step="1"
								class="h-2 w-full cursor-pointer appearance-none rounded-lg bg-violet-100"
							/>
							<div class="flex items-center rounded-full bg-violet-100 px-3 py-2 text-xl font-bold text-violet-900">
								{displayAmount(flexPrice)}€
							</div>
						</div>
					</div>
				{:else}
					<p class="label text-center">
						{ownerName} möchte für diesen Event Eintritt
					</p>
				{/if}

				<div class="my-4">
					{#if guestPaidAlreadyButAddedCompanions}
						<div class=" text-xl font-medium text-violet-950">
							<div class="">
								<div class="grid grid-cols-6 text-right">
									<span class=" col-span-3">
										{attendingPersons} Personen <span class="mx-[0.3rem]">×</span>
										{displayAmount(flexPrice)}€
									</span>
									<span class="">=</span>
									<span class=" col-span-2 pr-3">
										{totalPriceStr}
									</span>
								</div>
								<div class="my-4 grid grid-cols-6 text-right">
									<span class="col-span-3"> Bereits gezahlt </span>
									<span class="">=</span>
									<span class="col-span-2 pr-3">
										{displayAmount(guest.paidAllegedly)}€
									</span>
								</div>
								<div class="grid grid-cols-6 text-right">
									<span class="col-span-3"> Noch zu zahlen </span>
									<span class="">=</span>
									<div class="col-span-2 -mt-2 inline-block" data-testid="total-price">
										<div class="inline-block rounded-full bg-violet-100 px-3 py-2 font-bold">
											{displayAmount(diffPrice)}€
										</div>
									</div>
								</div>
							</div>
						</div>
					{:else if attendingPersons > 1}
						<div class="flex items-center justify-center text-xl font-medium text-violet-950">
							<div class="">
								{attendingPersons} Personen <span class="mx-[0.3rem]">×</span>
								{displayAmount(flexPrice)}€ <span class="ml-[0.3rem] mr-2">=</span>
							</div>
							<div
								class="flex flex-col items-center justify-center rounded-full bg-violet-100 px-3 py-2 text-violet-900"
							>
								<div class="font-bold" data-testid="total-price">
									{totalPriceStr}
								</div>
							</div>
						</div>
					{:else if !$currentEvent.maxPrice}
						<div class="flex items-center justify-center text-xl font-medium text-violet-950">
							<div
								class="flex flex-col items-center justify-center rounded-full bg-violet-100 px-3 py-2 text-violet-900"
							>
								<div class="font-bold" data-testid="total-price">
									{totalPriceStr}
								</div>
							</div>
						</div>
					{/if}

					<div class="my-6 text-left">
						<div>
							<span class="mr-3 inline-block">
								<span class="label">Schritt</span>
								<span class="inline-block h-6 w-6 rounded-full bg-violet-100 px-2 font-medium text-violet-800">1</span>
							</span>
							<br />
							<span class="inline-block">
								{#if guestPaidAlreadyButAddedCompanions}
									Überweise den restlichen Betrag per Paypal.
								{:else}
									Überweise das Geld per Paypal.
								{/if}
							</span>
						</div>

						<div class="mt-4 flex items-center justify-center gap-1">
							<IconWarning class=" size-6 text-violet-800"></IconWarning>
							<span class="label">Komm danach zurück für Schritt 2</span>
							<IconWarning class="size-6 text-violet-800"></IconWarning>
						</div>
						<div class=" mb-8 mt-2 flex justify-center">
							<a
								href={paypalLink(
									$currentEvent.paypalUsername ?? '',
									guestPaidAlreadyButAddedCompanions ? diffPrice : totalPrice
								)}
								target="_blank"
								class="btn btn-secondary"
								data-testid="pay-with-paypal-btn"
							>
								<IconPaypal class="inline-block h-6 w-6" /> Mit Paypal bezahlen
							</a>
						</div>

						<div>
							<span class="mr-3 inline-block">
								<span class="label">Schritt</span>
								<span class="inline-block h-6 w-6 rounded-full bg-violet-100 px-2 font-medium text-violet-800">2</span>
							</span>
							<br />
							<span class="inline-block"> Hast du das Geld überwiesen? </span>
						</div>

						<div class="mt-4 flex justify-center">
							<button
								onclick={() => {
									showPaymentModal = false;
									showDidNotPayModal = true;
								}}
								class="btn btn-primary mr-6 w-full"
								data-testid="did-not-pay-btn"
							>
								Nein
							</button>
							<LoadingButton
								loading={okButtonLoading}
								onClick={async () => {
									await submit(totalPrice);
									showPaymentModal = false;
								}}
								class="btn btn-primary w-full"
								data-testid="i-paid-btn"
							>
								Ja habe ich
							</LoadingButton>
						</div>
					</div>
				</div>
			</div>

			{#if $currentEvent.boxOfficeEnabled && $currentEvent.paypalUsername}
				<div class="mb-4 mt-8 flex flex-col justify-center text-gray-500">
					<p class="px-2 text-center">
						Falls du kein Paypal Zugang hast kannst du das Geld auch persönlich übergeben.
						<br />
						(Mehr Aufwand für {ownerName})
					</p>
					<button
						onclick={async () => {
							await submit(undefined);
							showPaymentModal = false;
						}}
						class="link mx-auto mt-2 text-violet-600"
						type="button"
					>
						Ich werde persönlich bezahlen
					</button>
				</div>
			{/if}
		</div>
	</Modal>
{/if}

{#if showDidNotPayModal}
	<Modal onclose={() => (showDidNotPayModal = false)} classs="max-w-sm" header={false}>
		<div class="text-center">
			<p class="mb-6">Um an dem Event teilnehmen zu können musst du zuerst den Eintritt bezahlen.</p>
			<p>Bitte melde dich erneut an wenn du bereit bist den Eintritt zu bezahlen.</p>
		</div>

		<div class="mt-6 flex justify-center">
			<button
				onclick={() => {
					showDidNotPayModal = false;
					showModal = false;
				}}
				class="btn btn-primary">Okay</button
			>
		</div>
	</Modal>
{/if}

{#if showAddedToWaitlistDialog}
	<Modal onclose={() => (showAddedToWaitlistDialog = false)} classs="max-w-md" header={false}>
		<div class="text-center">
			<p class="mb-2">{m.attendingPrompt_eventFull()}</p>
			<p>{m.attendingPrompt_addedToWaitlist()}</p>
		</div>

		<div class="mt-6 flex justify-center">
			<button onclick={() => (showAddedToWaitlistDialog = false)} class="btn btn-primary">
				{m.attendingPrompt_okay()}
			</button>
		</div>
	</Modal>
{/if}
